/**
 * Create Team Page - Create teams against projects
 */
import { useState, useEffect } from 'react';
import { Users, Loader, Search, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { teamAPI } from '@/api/teamAPI';
import { organizationAPI, Organization } from '@/api/organizationAPI';
import { Project, User } from '@/types';
import { projectAPI } from '@/api/projectAPI';
import { userAPI } from '@/api/userAPI';

interface TeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void | Promise<void>;
  defaultOrganizationId?: string;
}

interface FormErrors {
  name?: string;
  organization?: string;
  project?: string;
  users?: string;
}

export const TeamModal = ({ onClose, onCreated, defaultOrganizationId }: TeamModalProps) => {
  // Form state
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [organizationId, setOrganizationId] = useState(defaultOrganizationId || '');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  
  // Data loading state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  
  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const orgs = await organizationAPI.getOrganizations({ limit: 100 });
        setOrganizations(orgs);
      } catch (err: any) {
        toast.error('Failed to load organizations');
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrganizations();
  }, []);
  
  // Fetch projects when organization is selected
  useEffect(() => {
    const fetchProjects = async () => {
      if (!organizationId) {
        setProjects([]);
        setSelectedProjectIds([]);
        return;
      }
      
      try {
        setLoadingProjects(true);
        const projs = await projectAPI.getProjects({ 
          organization_id: organizationId,
          is_active: true,
          limit: 100 
        });
        setProjects(projs);
      } catch (err: any) {
        toast.error('Failed to load projects');
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [organizationId]);
  
  // Fetch users when organization is selected (scoped by organization)
  useEffect(() => {
    const fetchUsers = async () => {
      if (!organizationId) {
        setUsers([]);
        setSelectedUserIds([]);
        return;
      }

      try {
        setLoadingUsers(true);
        const usrs = await userAPI.getUsers({ 
          organization_id: organizationId,
          is_active: true,
          limit: 100 
        });
        setUsers(usrs);
      } catch (err: any) {
        toast.error('Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [organizationId]);
  
  // Client-side validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (name.trim().length < 3) {
      newErrors.name = 'Team name must be at least 3 characters';
    } else if (name.trim().length > 200) {
      newErrors.name = 'Team name must be less than 200 characters';
    }
    
    if (!organizationId) {
      newErrors.organization = 'Organization is required';
    }
    
    if (selectedProjectIds.length === 0) {
      newErrors.project = 'At least one project must be selected';
    }
    
    if (selectedUserIds.length === 0) {
      newErrors.users = 'At least one user must be selected';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Handle team creation
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      await teamAPI.createTeam({
        name: name.trim(),
        description: description.trim() || undefined,
        organization_id: organizationId,
        project_ids: selectedProjectIds,
        user_ids: selectedUserIds,
      });
      
      toast.success('Team created successfully!');

      // Notify parent so it can refresh lists
      if (onCreated) {
        try {
          await onCreated();
        } catch (callbackError) {
          console.error('Error in TeamModal onCreated callback:', callbackError);
        }
      }
      
      // Reset form
      setName('');
      setDescription('');
      setOrganizationId('');
      setSelectedProjectIds([]);
      setSelectedUserIds([]);
      
      // Navigate back to teams or settings
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        'Failed to create team. Please try again.';
      toast.error(errorMessage);
      
      // Set specific field errors if available
      if (error?.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (detail.toLowerCase().includes('name')) {
          setErrors({ name: detail });
        } else if (detail.toLowerCase().includes('organization')) {
          setErrors({ organization: detail });
        } else if (detail.toLowerCase().includes('project')) {
          setErrors({ project: detail });
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleUserToggle = (userId: string) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
    
    // Clear error when user selects at least one user
    if (errors.users && selectedUserIds.length === 0) {
      setErrors({ ...errors, users: undefined });
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">  
      {/* Form */}
      <div className="card w-full max-w-4xl h-[90vh] flex flex-col p-0">
        <div className="flex flex-col mb-4 p-4">
          <h1 className="text-2xl font-bold text-gray-900">Create Team</h1>
        </div>
        
        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          {/* Scrollable content */}
          <div className="space-y-4 flex-1 overflow-y-auto pr-1 p-4">
            {/* Team Name */}
            <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Team Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: undefined });
                }
              }}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="Enter team name"
              disabled={loading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>
          
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input w-full"
              placeholder="Enter team description"
              rows={3}
              disabled={loading}
            />
          </div>
          
          {/* Organization */}
          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
              Organization <span className="text-red-500">*</span>
            </label>
            <select
              id="organization"
              value={organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value);
                setSelectedProjectIds([]); // Reset projects when organization changes
                setSelectedUserIds([]); // Reset users when organization changes
                if (errors.organization) {
                  setErrors({ ...errors, organization: undefined });
                }
              }}
              className={`input w-full ${errors.organization ? 'border-red-500' : ''}`}
              disabled={loading || loadingOrgs}
              required
            >
              <option value="">Select organization...</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
            {errors.organization && (
              <p className="mt-1 text-sm text-red-600">{errors.organization}</p>
            )}
          </div>
          
          {/* Projects - multi-select dropdown-style list */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Projects <span className="text-red-500">*</span>
            </label>

            <button
              type="button"
              onClick={() => setShowProjectPicker((prev) => !prev)}
              disabled={loading || loadingProjects || !organizationId}
              className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-sm bg-white hover:bg-gray-50 transition ${
                errors.project ? 'border-red-500' : 'border-gray-300'
              } ${!organizationId ? 'cursor-not-allowed text-gray-400 bg-gray-50' : 'cursor-pointer'}`}
            >
              <span className="truncate text-left">
                {!organizationId
                  ? 'Select organization first'
                  : selectedProjectIds.length === 0
                    ? 'Select projects...'
                    : `${selectedProjectIds.length} project${
                        selectedProjectIds.length !== 1 ? 's' : ''
                      } selected`}
              </span>
              <ChevronDown
                className={`w-4 h-4 text-gray-500 transition-transform ${
                  showProjectPicker ? 'rotate-180' : ''
                }`}
              />
            </button>

            {errors.project && (
              <p className="mt-1 text-sm text-red-600">{errors.project}</p>
            )}

            {showProjectPicker && organizationId && (
              <div className="mt-2 bg-white rounded-lg border border-gray-200 max-h-[40vh] flex flex-col overflow-hidden">
                {/* Search */}
                <div className="px-4 py-3 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by name or key..."
                      value={projectSearchQuery}
                      onChange={(e) => setProjectSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                {/* Selected Count */}
                {selectedProjectIds.length > 0 && (
                  <div className="mx-4 mt-3 mb-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
                    <p className="text-sm text-primary-700">
                      <strong>{selectedProjectIds.length}</strong> project
                      {selectedProjectIds.length !== 1 ? 's' : ''} selected
                    </p>
                  </div>
                )}

                {/* Projects List */}
                <div className="flex-1 overflow-y-auto mt-2">
                  {loadingProjects ? (
                    <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Loading projects...</span>
                    </div>
                  ) : projects.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No projects found for this organization
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {projects
                        .filter((project) => {
                          if (!projectSearchQuery.trim()) return true;
                          const q = projectSearchQuery.toLowerCase();
                          return (
                            project.name.toLowerCase().includes(q) ||
                            project.key.toLowerCase().includes(q)
                          );
                        })
                        .map((project) => {
                          const checked = selectedProjectIds.includes(project.id);
                          return (
                            <label
                              key={project.id}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setSelectedProjectIds((prev) => {
                                    const exists = prev.includes(project.id);
                                    const next = exists
                                      ? prev.filter((id) => id !== project.id)
                                      : [...prev, project.id];
                                    if (errors.project && next.length > 0) {
                                      setErrors({ ...errors, project: undefined });
                                    }
                                    return next;
                                  });
                                }}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                disabled={loading}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {project.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  Key: {project.key}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Users - multi-select dropdown-style list */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team Members <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setShowUserPicker((prev) => !prev)}
              disabled={loadingUsers || !organizationId}
              className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-sm bg-white hover:bg-gray-50 transition ${
                !organizationId ? 'border-gray-300 cursor-not-allowed text-gray-400 bg-gray-50' : 'border-gray-300 cursor-pointer'
              }`}
            >
              <span className="truncate text-left">
                {!organizationId
                  ? 'Select organization first'
                  : loadingUsers
                    ? 'Loading users...'
                    : selectedUserIds.length === 0
                      ? 'Select team members...'
                      : `${selectedUserIds.length} user${
                          selectedUserIds.length !== 1 ? 's' : ''
                        } selected`}
              </span>
              <ChevronDown
                className={`w-4 h-4 text-gray-500 transition-transform ${
                  showUserPicker ? 'rotate-180' : ''
                }`}
              />
            </button>

            {showUserPicker && (
              <div className="mt-2 bg-white rounded-lg border border-gray-200 max-h-[40vh] flex flex-col overflow-hidden">
                {/* Search */}
                <div className="px-4 py-3 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by name, email, or username..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                {/* Selected Count */}
                {selectedUserIds.length > 0 && (
                  <div className="mx-4 mt-3 mb-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
                    <p className="text-sm text-primary-700">
                      <strong>{selectedUserIds.length}</strong> user
                      {selectedUserIds.length !== 1 ? 's' : ''} selected
                    </p>
                  </div>
                )}

                {/* Available Users List */}
                <div className="flex-1 overflow-y-auto mt-2">
                  {loadingUsers ? (
                    <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Loading users...</span>
                    </div>
                  ) : users.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No users found
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {users
                        .filter((user) => {
                          if (!userSearchQuery.trim()) return true;
                          const q = userSearchQuery.toLowerCase();
                          const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
                          return (
                            user.email.toLowerCase().includes(q) ||
                            user.username.toLowerCase().includes(q) ||
                            fullName.includes(q)
                          );
                        })
                        .map((user) => (
                          <label
                            key={user.id}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(user.id)}
                              onChange={() => handleUserToggle(user.id)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              disabled={loading}
                            />
                            <div className="flex-shrink-0">
                              {user.avatar_url ? (
                                <img
                                  className="h-8 w-8 rounded-full"
                                  src={user.avatar_url}
                                  alt=""
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-medium">
                                  {user.username.substring(0, 2).toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {user.display_name || `${user.first_name} ${user.last_name}`}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                @{user.username} • {user.email}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 capitalize">
                                {user.role.replace('_', ' ')}
                              </span>
                            </div>
                          </label>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {errors.users && (
              <p className="mt-1 text-sm text-red-600">{errors.users}</p>
            )}
            </div>
          </div>
          
          {/* Submit Button - pinned at bottom */}
          <div className="flex gap-3 pt-4 mt-4 border-t border-gray-200 flex-shrink-0 p-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  Create Team
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


